import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Transient network / edge-proxy errors that we should retry silently.
// Cloudflare 520–524, Bad Gateway 502, Unavailable 503, Gateway Timeout 504,
// plus plain network drops (no status code at all).
const TRANSIENT_STATUS = new Set([502, 503, 504, 520, 521, 522, 523, 524, 408]);

function isTransient(err) {
  if (!err) return false;
  // No response at all → network drop, DNS blip, Cloudflare abort mid-stream
  if (!err.response) return true;
  return TRANSIENT_STATUS.has(err.response.status);
}

async function _sleep(ms) {
  return new Promise(r => setTimeout(r, ms));
}

// axios wrapper with up to N retries on transient errors, exponential-ish backoff
async function _requestWithRetry(fn, { maxAttempts = 4, baseDelayMs = 1500 } = {}) {
  let lastErr;
  for (let attempt = 1; attempt <= maxAttempts; attempt++) {
    try {
      return await fn();
    } catch (err) {
      lastErr = err;
      if (!isTransient(err) || attempt === maxAttempts) throw err;
      const delay = baseDelayMs * attempt; // 1.5s, 3s, 4.5s
      await _sleep(delay);
    }
  }
  throw lastErr;
}

/**
 * Run a tool that uses the async-job pattern:
 *   1. POST starts job → { job_id, status: "running" }
 *   2. We poll GET /tools/jobs/{id} every ~4s until completed or failed
 * Returns the result.data of the completed job.
 *
 * Robustness:
 *   - Initial POST retried up to 4× on transient errors (520/502/503/504/network).
 *   - Each poll retried internally — a single failed poll no longer aborts the run.
 *   - Consecutive poll-error budget before giving up: 30 (≈ 2-3 min of continuous
 *     Cloudflare 520s or mobile-tab-throttled connection drops). Carousel renders
 *     can take 3-4 min via fal.ai fallback, so we MUST tolerate long edge-network
 *     hiccups without bailing — the job keeps running on the server regardless.
 *
 * Usage:
 *   const data = await runToolJob("post", "/tools/comment-goldmine", { url });
 *   const data = await runToolJob("get",  "/tools/instagram-trending");
 *
 * Optional onTick({elapsedSec, status, etaSec}) for live UI progress.
 */
export async function runToolJob(method, path, body = null,
                                 { maxWaitMs = 600000, intervalMs = 4000, onTick, etaSec = 60 } = {}) {
  const start = await _requestWithRetry(() =>
    method === "post"
      ? axios.post(`${API}${path}`, body || {}, { timeout: 60000 })
      : axios.get(`${API}${path}`, { timeout: 60000 })
  );

  // If endpoint already returned a final result (sync tools), pass through.
  if (!start.data?.job_id) return start.data;

  const jobId = start.data.job_id;
  const startTs = Date.now();
  const deadline = startTs + maxWaitMs;
  let consecutivePollErrors = 0;
  // Allow up to ~2-3 min of continuous polling failures before giving up. The
  // backend job is independent of the polling connection, so a Cloudflare blip,
  // mobile-tab throttling, or transient 520 must NOT kill the user experience.
  const MAX_CONSEC_POLL_ERRORS = 30;

  while (Date.now() < deadline) {
    await _sleep(intervalMs);
    const elapsedSec = Math.floor((Date.now() - startTs) / 1000);
    let job;
    try {
      const resp = await _requestWithRetry(() => axios.get(`${API}/tools/jobs/${jobId}`, { timeout: 15000 }),
                                           { maxAttempts: 3, baseDelayMs: 1500 });
      job = resp.data;
      consecutivePollErrors = 0;
    } catch (err) {
      // Even after retries this poll failed. Count it, continue polling.
      consecutivePollErrors += 1;
      if (consecutivePollErrors >= MAX_CONSEC_POLL_ERRORS) {
        const err2 = new Error(
          'Die Internet-Verbindung wackelt gerade — dein Karussell wird aber im ' +
          'Hintergrund fertig erstellt! Du findest es in 2-3 Minuten in deiner Carousel-Historie. ' +
          'Bitte einfach kurz warten und die Seite neu laden.'
        );
        err2.code = "POLL_LOST_BACKGROUND_RUNNING";
        err2.jobId = jobId;
        throw err2;
      }
      onTick && onTick({ elapsedSec, status: "running", etaSec });
      continue;
    }

    onTick && onTick({ elapsedSec, status: job.status, etaSec });
    if (job.status === "completed") {
      const result = job.result || {};
      try {
        Object.defineProperty(result, "__jobId", {
          value: jobId, enumerable: false, writable: true, configurable: true,
        });
      } catch (_) { /* result not extensible */ }
      return result;
    }
    if (job.status === "failed") throw new Error(job.error || "Job fehlgeschlagen");
  }
  const err3 = new Error(
    'Die Generierung dauert ungewöhnlich lange (>10 Min). ' +
    'Dein Karussell wird im Hintergrund fertig — schau in 2 Min in deine Carousel-Historie.'
  );
  err3.code = "POLL_DEADLINE_BACKGROUND_RUNNING";
  err3.jobId = jobId;
  throw err3;
}

/**
 * Per-tool ETA in seconds (typical observed Apify+AI runtime).
 * Used to show a soft progress hint to the user.
 */
export const TOOL_ETA = {
  "comment-goldmine": 180,
  "instagram-trending": 240,
  "instagram-reels-search": 120,
  "facebook-ads-spy": 120,
  "facebook-page-audit": 180,
  "facebook-group-listener": 150,
};
